// batch-71 kennismaking (FO-534, spec 234) - PURE regels van de kennismaking voor de
// start: het moment van het gesprek, de lijst met statussen en de tekst van het
// verslag. Geen DB, geen IO: de motor (`apps/api/src/services/kennismaking/`) levert de
// feiten aan en voert uit wat hier wordt uitgerekend.
//
// Datums zijn NL-kalenderdagen ('yyyy-mm-dd'), tijden NL-wandklok ('HH:mm'). Werkdagen
// volgens de feestdagentabel; DST valt buiten deze rekensom omdat er alleen met
// wandklok wordt gerekend en de omzetting naar een instant in `amsterdam-tijd` zit.

import {
  KENNISMAKING_AUTO_STATUS_LABELS,
  KENNISMAKING_GROEP_LABELS,
  KENNISMAKING_GROEPEN,
  type KennismakingAutoStatus,
  type KennismakingDetailDTO,
  type KennismakingGroep,
  type KennismakingRegelDTO,
  type KennismakingVoortgangDTO,
} from '@streep/types'
import { contractDagPlusDagen, contractWerkdagenVooruit } from './contract-mijlpalen'
import { parseDagDatum } from './dagtaken-datum'
import { isWerkdag } from './uren-calculations'

// ── Het moment van het gesprek ───────────────────────────────────────────────

export interface KennismakingMomentInvoer {
  /** De eerste dag die in aanmerking komt (het moment van de mijlpaal, of vandaag). */
  vanafDag: string
  /** De laatste werkdag voor de start. */
  uiterlijkDag: string
  /** Feestdagen als 'yyyy-mm-dd'; tellen niet als werkdag. */
  feestdagen: readonly string[]
  /** Dagen waarop de verantwoordelijke op de standaardtijd niet kan. */
  bezet: ReadonlySet<string>
}

export interface KennismakingMoment {
  dag: string
  /** Er was geen vrije werkdag: het gesprek staat op de eerste werkdag en moet nog verplaatst worden. */
  moetVerplaatsen: boolean
}

function isWerkdagVanLijst(dag: string, feestdagen: readonly string[]): boolean {
  return isWerkdag(parseDagDatum(dag), feestdagen.map((f) => parseDagDatum(f)))
}

/** De werkdag op of na `dag`. */
export function kennismakingWerkdagOpOfNa(dag: string, feestdagen: readonly string[]): string {
  return isWerkdagVanLijst(dag, feestdagen) ? dag : contractWerkdagenVooruit(dag, 1, feestdagen)
}

/**
 * De eerste werkdag vanaf `vanafDag` waarop de verantwoordelijke vrij is, uiterlijk de
 * laatste werkdag voor de start (FO-534 §A2). Is er geen vrije dag, dan de eerste
 * werkdag met de vlag "verplaatsen" (de actie blijft staan, nooit stil geen afspraak).
 */
export function kiesKennismakingMoment(invoer: KennismakingMomentInvoer): KennismakingMoment {
  const eerste = kennismakingWerkdagOpOfNa(invoer.vanafDag, invoer.feestdagen)
  let dag = eerste
  for (let i = 0; i < 60 && dag <= invoer.uiterlijkDag; i++) {
    if (isWerkdagVanLijst(dag, invoer.feestdagen) && !invoer.bezet.has(dag)) {
      return { dag, moetVerplaatsen: false }
    }
    dag = contractDagPlusDagen(dag, 1)
  }
  return { dag: eerste, moetVerplaatsen: true }
}

/** De kandidaat-dagen voor de beschikbaarheidscheck (alleen werkdagen). */
export function kennismakingKandidaatDagen(
  vanafDag: string,
  uiterlijkDag: string,
  feestdagen: readonly string[],
): string[] {
  const uit: string[] = []
  let dag = kennismakingWerkdagOpOfNa(vanafDag, feestdagen)
  for (let i = 0; i < 60 && dag <= uiterlijkDag; i++) {
    if (isWerkdagVanLijst(dag, feestdagen)) uit.push(dag)
    dag = contractDagPlusDagen(dag, 1)
  }
  return uit
}

/** Een tijd uit Outlook op het kwartier leggen: werkbon-tijden staan altijd op het kwartier. */
export function kennismakingOpKwartier(hm: string): string {
  const [uurTekst, minuutTekst] = hm.split(':')
  const totaal = Number(uurTekst) * 60 + Number(minuutTekst)
  const afgerond = Math.floor(totaal / 15) * 15
  return `${String(Math.floor(afgerond / 60)).padStart(2, '0')}:${String(afgerond % 60).padStart(2, '0')}`
}

/** De duur uit Outlook op het kwartier, minstens een kwartier. */
export function kennismakingDuurOpKwartier(minuten: number): number {
  return Math.max(15, Math.round(minuten / 15) * 15)
}

/** De eindtijd bij een begintijd en een duur (niet over middernacht heen). */
export function kennismakingEindTijd(hm: string, duurMinuten: number): string {
  const [uurTekst, minuutTekst] = hm.split(':')
  const totaal = Math.min(23 * 60 + 45, Number(uurTekst) * 60 + Number(minuutTekst) + duurMinuten)
  return `${String(Math.floor(totaal / 60)).padStart(2, '0')}:${String(totaal % 60).padStart(2, '0')}`
}

/** Kalenderdagen van `vandaag` tot `dag` (0 = vandaag, negatief = voorbij). */
export function kennismakingDagenTot(dag: string, vandaag: string): number {
  return Math.round((parseDagDatum(dag).getTime() - parseDagDatum(vandaag).getTime()) / 86_400_000)
}

/** "vandaag", "morgen" of "over 3 dagen" voor de dashboard-kaart. */
export function kennismakingAfstandTekst(dagen: number): string {
  if (dagen <= 0) return 'vandaag'
  if (dagen === 1) return 'morgen'
  return `over ${dagen} dagen`
}

// ── De lijst ─────────────────────────────────────────────────────────────────

/** Wat een check over een onderwerp zegt, of niets als er geen check bij hoort. */
export interface KennismakingRegelStand {
  status: KennismakingAutoStatus
  detail: string | null
  bronHref: string | null
  bronLabel: string | null
  details: KennismakingDetailDTO[]
}

export interface KennismakingLijstRegelInvoer {
  id: string
  code: string
  groep: KennismakingGroep
  label: string
  toelichting: string | null
  vrijeTekst: boolean
  checkSleutel: string | null
  besproken: boolean
  besprokenOp: Date | string | null
  nietVanToepassing: boolean
  nvtReden: string | null
  notitie: string | null
  volgorde: number
}

const GEEN_CHECK: KennismakingRegelStand = {
  status: 'GEEN_BRON',
  detail: null,
  bronHref: null,
  bronLabel: null,
  details: [],
}

/**
 * Een check zonder uitkomst staat op ONBEKEND, nooit op groen (FO-534 §6.7). Ook als
 * de lijst een regel met een check kent waarvan de uitkomst om wat voor reden dan ook
 * ontbreekt: het ontbreken van een antwoord is geen goed antwoord.
 */
const CHECK_ONTBREEKT: KennismakingRegelStand = {
  status: 'ONBEKEND',
  detail: 'De status kon niet worden vastgesteld.',
  bronHref: null,
  bronLabel: null,
  details: [],
}

/**
 * Bouw de regels van de lijst uit het sjabloon en de bronnen (FO-534 §11). Puur: de
 * uitkomsten van de checks komen binnen als `standen` per regel-id.
 */
export function bouwKennismakingLijst(
  regels: readonly KennismakingLijstRegelInvoer[],
  standen: ReadonlyMap<string, KennismakingRegelStand>,
  extra: {
    actiepunten?: ReadonlyMap<string, KennismakingRegelDTO['actiepunten']>
    standaardHouders?: ReadonlyMap<string, KennismakingRegelDTO['standaardHouder']>
  } = {},
): KennismakingRegelDTO[] {
  const groepVolgorde = new Map<KennismakingGroep, number>(KENNISMAKING_GROEPEN.map((g, i) => [g, i]))
  return [...regels]
    .sort(
      (a, b) =>
        (groepVolgorde.get(a.groep) ?? 99) - (groepVolgorde.get(b.groep) ?? 99) || a.volgorde - b.volgorde,
    )
    .map((r): KennismakingRegelDTO => {
      const stand = r.checkSleutel === null ? GEEN_CHECK : (standen.get(r.id) ?? CHECK_ONTBREEKT)
      return {
        id: r.id,
        code: r.code,
        groep: r.groep,
        label: r.label,
        toelichting: r.toelichting,
        vrijeTekst: r.vrijeTekst,
        status: stand.status,
        statusLabel: KENNISMAKING_AUTO_STATUS_LABELS[stand.status],
        statusDetail: stand.detail,
        bronHref: stand.bronHref,
        bronLabel: stand.bronLabel,
        details: stand.details,
        besproken: r.besproken,
        besprokenOp:
          r.besprokenOp === null
            ? null
            : typeof r.besprokenOp === 'string'
              ? r.besprokenOp
              : r.besprokenOp.toISOString(),
        nietVanToepassing: r.nietVanToepassing,
        nvtReden: r.nvtReden,
        notitie: r.notitie,
        actiepunten: extra.actiepunten?.get(r.id) ?? [],
        standaardHouder: extra.standaardHouders?.get(r.id) ?? null,
      }
    })
}

/**
 * Twee kleuren per regel (FO-534 §B7): geregeld (automatisch) en besproken (vinkje).
 * Een regel op niet van toepassing telt nergens mee. Een vrij tekstveld telt niet mee
 * als "te bespreken": dat is een notitieveld. ONBEKEND telt als niet geregeld.
 */
export function kennismakingVoortgang(
  regels: ReadonlyArray<Pick<KennismakingRegelDTO, 'status' | 'besproken' | 'nietVanToepassing' | 'vrijeTekst'>>,
): KennismakingVoortgangDTO {
  const voortgang: KennismakingVoortgangDTO = {
    geregeld: 0,
    geregeldTotaal: 0,
    besproken: 0,
    besprokenTotaal: 0,
    onbekend: 0,
    nietVanToepassing: 0,
  }
  for (const r of regels) {
    if (r.nietVanToepassing) {
      voortgang.nietVanToepassing += 1
      continue
    }
    if (r.status === 'ONBEKEND') voortgang.onbekend += 1
    if (r.status !== 'GEEN_BRON') {
      voortgang.geregeldTotaal += 1
      if (r.status === 'GEREGELD') voortgang.geregeld += 1
    }
    if (!r.vrijeTekst) {
      voortgang.besprokenTotaal += 1
      if (r.besproken) voortgang.besproken += 1
    }
  }
  return voortgang
}

// ── Het verslag ──────────────────────────────────────────────────────────────

export interface KennismakingVerslagInvoer {
  medewerkerNaam: string
  startDatum: string
  regels: ReadonlyArray<
    Pick<KennismakingRegelDTO, 'groep' | 'label' | 'statusLabel' | 'statusDetail' | 'status' | 'besproken' | 'nietVanToepassing' | 'nvtReden' | 'notitie' | 'vrijeTekst'>
  >
}

function nlDatumKort(dag: string): string {
  const [jaar, maand, d] = dag.split('-')
  return `${d}-${maand}-${jaar}`
}

/**
 * De tekst van het verslag: per groep de onderwerpen met besproken en de stand (FO-534
 * §B9). Komt in het v2-veld `verslag` van het gespreksverslag via de bestaande
 * verslag-API, en dat verslag gaat naar de medewerker. Daarom staan hier NOOIT de notities
 * van de gespreksvoerder, de vrije velden (vragen, bijzonderheden) en de vrij getypte reden
 * bij n.v.t.: die blijven intern op de nalooplijst. Statusdetails bevatten alleen tellingen,
 * namen van middelen en datums.
 */
export function bouwKennismakingVerslagTekst(invoer: KennismakingVerslagInvoer): string {
  const regelsPerGroep = new Map<KennismakingGroep, string[]>()
  for (const r of invoer.regels) {
    let tekst: string
    if (r.vrijeTekst) continue
    if (r.nietVanToepassing) {
      tekst = `- ${r.label}: niet van toepassing`
    } else {
      const delen: string[] = [r.besproken ? 'besproken' : 'niet besproken']
      if (r.status !== 'GEEN_BRON') {
        delen.push(`${r.statusLabel.toLowerCase()}${r.statusDetail ? ` (${r.statusDetail})` : ''}`)
      }
      tekst = `- ${r.label}: ${delen.join(', ')}`
    }
    const lijst = regelsPerGroep.get(r.groep) ?? []
    lijst.push(tekst)
    regelsPerGroep.set(r.groep, lijst)
  }
  const blokken: string[] = [
    `Kennismaking met ${invoer.medewerkerNaam}, start ${nlDatumKort(invoer.startDatum)}.`,
  ]
  for (const groep of KENNISMAKING_GROEPEN) {
    const lijst = regelsPerGroep.get(groep)
    if (!lijst || lijst.length === 0) continue
    blokken.push(`${KENNISMAKING_GROEP_LABELS[groep]}\n${lijst.join('\n')}`)
  }
  return blokken.join('\n\n')
}

/** Mag de lijst worden afgerond? Geeft de reden terug als dat niet kan. */
export function kennismakingAfrondBlokkade(invoer: {
  status: 'OPEN' | 'AFGEROND' | 'GEANNULEERD'
  heeftGesprek: boolean
}): string | null {
  if (invoer.status === 'AFGEROND') return 'Deze kennismaking is al afgerond.'
  if (invoer.status === 'GEANNULEERD') return 'Deze kennismaking is afgezegd.'
  if (!invoer.heeftGesprek) return 'Er is geen gesprek gepland. Plan eerst de kennismaking.'
  return null
}
